import NiceModal from '@ebay/nice-modal-react';
import { createFileRoute, notFound, useRouter } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cancelTransition, getDepositInfo } from '@/api/general';
import {
  countDownSecondsAtom,
  deadlineAtom,
  pollingIntervalIdAtom,
  servicesUrlAtom,
  statusAtom,
  transitionIdAtom,
} from '@/atoms/general';
import CardHeader from '@/components/card-header';
import ContactButton from '@/components/contact-button';
import HintSection from '@/components/hint-section';
import OrderNumberSection from '@/components/order-number-section';
import PageLoadingSpinner from '@/components/page-loading-spinner';
import TransferDetailPay from '@/components/transfer-detail-pay';
import TransitionAmountSection from '@/components/transition-amount-section';
import { POLLING_INTERVAL } from '@/config';
import { DEPOSIT_CATEGORY, DEPOSIT_METHOD, PAY, PAY_QR, PAYMENT_TYPE, type PaymentCategory } from '@/constants/general';
import { PAY_STATUS } from '@/constants/status';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils';

export const Route = createFileRoute('/_layout/pay/$id')({
  loader: async ({ params: { id } }) => {
    const paymentInfo = await getDepositInfo(id);
    if (!paymentInfo) throw notFound();

    return paymentInfo;
  },
  pendingComponent: () => <PageLoadingSpinner />,
  component: Pay,
});

function Pay() {
  const paymentInfo = Route.useLoaderData();
  const { id } = Route.useParams();
  const router = useRouter();
  const { t } = useTranslation();
  const [pollingIntervalId, setPollingIntervalId] = useAtom(pollingIntervalIdAtom);
  const setTransitionId = useSetAtom(transitionIdAtom);
  const setServicesUrl = useSetAtom(servicesUrlAtom);
  const [status, setStatus] = useAtom(statusAtom);
  const setCountDownSeconds = useSetAtom(countDownSecondsAtom);
  const [deadline, setDeadline] = useAtom(deadlineAtom);

  const [paymentCategory, setPaymentCategory] = useState<PaymentCategory | ''>('');
  const [isQrPayment, setIsQrPayment] = useState<boolean>(false);

  useEffect(() => {
    setTransitionId(id);
    setServicesUrl(paymentInfo.merchant_cs_url);
    setPaymentCategory(paymentInfo.app_code.split('_')[0] as PaymentCategory);

    const method = paymentInfo.app_code.split('_')[1];
    setIsQrPayment(method === DEPOSIT_METHOD.QR || method === DEPOSIT_METHOD.QR_TRANSFER);
  }, [id, paymentInfo]);

  useEffect(() => {
    const deadline = dayjs(paymentInfo.transfer_deadline);
    const calculatedCountDownSeconds = Math.floor((deadline.toDate().getTime() - new Date().getTime()) / 1000);

    setCountDownSeconds(calculatedCountDownSeconds);
    setDeadline(deadline);

    if (calculatedCountDownSeconds <= 0 && paymentInfo.web_status === PAY_STATUS.PENDING_TRANSFER) {
      setStatus(PAY_STATUS.TIMEOUT);
    } else {
      setStatus(paymentInfo.web_status);

      if (paymentInfo.web_status === PAY_STATUS.PENDING_TRANSFER && calculatedCountDownSeconds > 0) {
        if (paymentCategory === DEPOSIT_CATEGORY.ECNY) {
          NiceModal.show('ecny-warning-modal', { amount: formatCurrency(paymentInfo.amount) });
        }
        if (paymentCategory === DEPOSIT_CATEGORY.ALIPAY) {
          NiceModal.show('alipay-warning-modal');
        }
      }
    }
  }, [paymentInfo, paymentCategory]);

  useEffect(() => {
    if (pollingIntervalId) return;

    const intervalId = setInterval(() => {
      router.invalidate();
    }, POLLING_INTERVAL);

    setPollingIntervalId(intervalId);

    return () => {
      clearInterval(intervalId);
      setPollingIntervalId(null);
    };
  }, [router, setPollingIntervalId]);

  return (
    <main className="flex items-center justify-center main-height p-3 sm:p-6 md:p-10 bg-main-gray relative overflow-y-scroll">
      <div className={cn('device-height w-[340px] z-10 bg-main-white rounded-xl shadow-custom relative flex flex-col')}>
        <ContactButton />
        <CardHeader />
        <div className="flex flex-col overflow-y-scroll flex-1">
          <div className="flex flex-col h-full">
            <OrderNumberSection orderId={paymentInfo?.merchant_order_id} createdTime={paymentInfo?.created_time} />
            <TransitionAmountSection amount={paymentInfo?.amount} />
            <TransferDetailPay
              qrContent={paymentInfo?.is_qr ? paymentInfo?.card_number : undefined}
              status={status}
              deadline={deadline}
              cardHolder={paymentInfo?.card_holder}
              cardNumber={paymentInfo?.card_number}
              bankName={paymentInfo?.bank_name}
              paymentCategory={paymentCategory}
              ifscCode={paymentInfo?.ifsc_code}
            />
            <HintSection
              type={
                paymentCategory === DEPOSIT_CATEGORY.ECNY
                  ? isQrPayment
                    ? 'digital-cny-scan'
                    : 'digital-cny'
                  : 'general'
              }
            />
          </div>
        </div>
        {status === PAY_STATUS.PENDING && (
          <section className="py-4 border-t flex divide-x divide-border-gray z-0 -bottom-[3.3rem] border-border-gray bg-main-white rounded-b-xl">
            <button
              onClick={() => {
                NiceModal.show('transition-cancel-modal', {
                  title: t('btn_cancel_tran_dialog'),
                  onConfirm: () => {
                    setStatus(PAY_STATUS.CANCELLING);
                    cancelTransition(paymentInfo.merchant_order_id);
                    router.invalidate();
                  },
                });
              }}
              className="w-full cursor-pointer text-button-inactive hover:text-button-active bg-main-white transition-colors text-sm font-semibold text-center"
            >
              {t('btn_cancel_tran')}
            </button>
          </section>
        )}
        {status === PAY_STATUS.PENDING_TRANSFER && (
          <section className="py-4 border-t flex divide-x divide-border-gray z-0 -bottom-[3.3rem] border-border-gray bg-main-white rounded-b-xl">
            {paymentCategory === DEPOSIT_CATEGORY.ECNY && (
              <button
                onClick={() => NiceModal.show('ecny-tutorial-modal')}
                className="flex-1 cursor-pointer text-[#5B78A1] hover:text-button-active bg-main-white transition-colors text-sm text-center"
              >
                {t('btn_digital_cny_tran_guide')}
              </button>
            )}
            <button
              onClick={() =>
                NiceModal.show('transition-image-upload-modal', {
                  id: paymentInfo.merchant_order_id,
                  onConfirm: () => router.invalidate(),
                  paymentType: PAYMENT_TYPE.PAY,
                })
              }
              className="flex-1 cursor-pointer text-button-inactive hover:text-button-active bg-main-white transition-colors text-sm font-semibold text-center"
            >
              {t('order_upload_tran_pic')}
            </button>
          </section>
        )}
      </div>
    </main>
  );
}
